import { useState } from "react"

import InputError from "@/components/input-error"
import ImageUpload from "@/components/ui/image-upload"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface PartnerOption {
  uid: string
  name: string
}

interface PartnerUserFormProps {
  data: {
    partner_user: {
      name: string
      email: string
      partner_uid: string
      avatar: File | null
      send_welcome_email?: string
    }
  }
  setData: (key: string, value: any) => void
  errors: Record<string, any>
  partners: PartnerOption[]
  mode?: "create" | "edit"
}

export default function PartnerUserForm({
  data,
  setData,
  errors,
  partners,
  mode = "create",
}: PartnerUserFormProps) {
  const [preview, setPreview] = useState<string | null>(null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      setData("partner_user", {
        ...data.partner_user,
        avatar: e.target.files[0],
      })
      setPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="partner">Partner account</Label>
        <Select
          value={data.partner_user.partner_uid}
          onValueChange={(val) =>
            setData("partner_user", {
              ...data.partner_user,
              partner_uid: val,
            })
          }
        >
          <SelectTrigger id="partner" className="w-full">
            <SelectValue placeholder="Select a partner" />
          </SelectTrigger>
          <SelectContent>
            {partners.map((p) => (
              <SelectItem key={p.uid} value={p.uid}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InputError message={(errors as any)?.partner_uid ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.partner_user.name}
          placeholder="Full name"
          onChange={(e) =>
            setData("partner_user", {
              ...data.partner_user,
              name: e.target.value,
            })
          }
        />
        <InputError message={(errors as any)?.name ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          className="mt-1 block w-full"
          defaultValue={data.partner_user.email}
          placeholder="email@example.com"
          onChange={(e) =>
            setData("partner_user", {
              ...data.partner_user,
              email: e.target.value,
            })
          }
        />
        <InputError message={(errors as any)?.email ?? undefined} />
      </div>

      <ImageUpload
        label="Avatar"
        id="avatar"
        preview={preview ?? undefined}
        onFileChange={handleFileChange}
        error={(errors as any)?.avatar ?? undefined}
        buttonLabel="Upload avatar"
      />

      {mode === "create" && (
        <div className="grid gap-2">
          <Label>Notification</Label>
          <RadioGroup
            value={data.partner_user.send_welcome_email}
            onValueChange={(val) =>
              setData("partner_user", {
                ...data.partner_user,
                send_welcome_email: val,
              })
            }
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="1" id="send_welcome_email_yes" />
              <Label htmlFor="send_welcome_email_yes" className="font-normal">
                Send welcome email
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="0" id="send_welcome_email_no" />
              <Label htmlFor="send_welcome_email_no" className="font-normal">
                Don&apos;t send email
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}
    </>
  )
}
